<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  DOCUMENT_CREATE_ERROR_I18N_KEYS,
  documentErrorMessage,
} from 'dashboard/helper/captainDocumentErrors';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DocumentForm from './DocumentForm.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  allowedWebHosts: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'createSuccess']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const i18nKey = 'CAPTAIN.DOCUMENTS.CREATE';
const canAddUrl = computed(() => props.allowedWebHosts.length > 0);
const dialogDescription = computed(() =>
  t(
    canAddUrl.value
      ? 'CAPTAIN.DOCUMENTS.FORM_DESCRIPTION'
      : 'CAPTAIN.DOCUMENTS.FORM_DESCRIPTION_FILES_ONLY'
  )
);

const handleSubmit = async newDocument => {
  try {
    await store.dispatch('captainDocuments/create', newDocument);
    emit('createSuccess');
    useAlert(t(`${i18nKey}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    useAlert(
      documentErrorMessage(
        error,
        t,
        `${i18nKey}.ERROR_MESSAGE`,
        DOCUMENT_CREATE_ERROR_I18N_KEYS
      )
    );
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <DocumentForm
      :assistant-id="assistantId"
      :allowed-web-hosts="allowedWebHosts"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
</template>
